import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Award, Paintbrush, Phone, Star } from "lucide-react";
import { MaskedLines, Reveal, spotlight, EASE } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import QuoteForm from "@/components/QuoteForm";
import { caledon, mcguire, orangeville } from "@/lib/images";
import { SERVICE_CATEGORIES } from "@/lib/services";

const GOOGLE_URL = "https://share.google/ne3cGjXAE31IWlowE";

function Stars({ size = 16, className = "text-gold" }) {
    return (
        <span className={`inline-flex gap-1 ${className}`} aria-label="5 out of 5 stars">
            {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} size={size} fill="currentColor" strokeWidth={0} />
            ))}
        </span>
    );
}

const FEATURED = [0, 1, 4, 6, 8, 10];

function Hero() {
    const { scrollY } = useScroll();
    const yImg = useTransform(scrollY, [0, 700], [0, 90]);
    const yCard = useTransform(scrollY, [0, 700], [0, -60]);

    return (
        <section className="relative overflow-hidden pt-32 pb-20 lg:min-h-screen lg:pt-40">
            <div
                aria-hidden
                className="pointer-events-none absolute -top-32 -right-40 h-[520px] w-[520px] rounded-full opacity-25 blur-3xl"
                style={{ background: "radial-gradient(circle, #C5A059 0%, transparent 65%)" }}
            />
            <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-8 lg:grid-cols-12 lg:px-16">
                <div className="lg:col-span-7">
                    <motion.p
                        initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
                        className="font-mono text-xs uppercase tracking-[0.3em] text-gold"
                    >
                        Ontario · Toronto &amp; the GTA
                    </motion.p>
                    <h1 className="font-display mt-6 text-[clamp(2.1rem,9vw,3.375rem)] font-extrabold leading-[1.02] tracking-tight sm:text-5xl xl:text-6xl">
                        <MaskedLines delay={0.15} lines={["Professional", "Painting.", "Exceptional", "Results."]} />
                    </h1>
                    <motion.div
                        initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.85, duration: 0.9, ease: EASE }}
                        className="mt-5 h-1.5 w-56 origin-left bg-gold sm:w-72"
                        style={{ clipPath: "polygon(0 0, 100% 0, 98% 100%, 2% 100%)" }}
                    />
                    <motion.p
                        initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.8, ease: EASE }}
                        className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
                    >
                        Quality painting services for homes, businesses, and construction projects
                        across the Greater Toronto Area.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.8, ease: EASE }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <Link to="/contact" data-testid="hero-cta-quote"
                            className="group inline-flex items-center gap-3 bg-gold px-8 py-4 font-display text-sm font-bold uppercase tracking-widest text-ink transition-transform hover:-translate-y-0.5">
                            Request a Free Quote
                            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                        </Link>
                        <Link to="/portfolio" data-testid="hero-cta-portfolio"
                            className="inline-flex items-center gap-3 border border-white/20 px-8 py-4 font-display text-sm font-bold uppercase tracking-widest text-slate-100 transition-colors hover:border-gold hover:text-gold">
                            View Our Work
                        </Link>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.8 }}
                        className="mt-12 flex flex-wrap gap-x-10 gap-y-4 border-t border-white/10 pt-6"
                    >
                        <a href={GOOGLE_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-google-stat" className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-slate-300 transition-colors hover:text-gold">
                            <Star size={14} className="text-gold" fill="currentColor" strokeWidth={0} /> 5.0 Google rating · 10 reviews
                        </a>
                        <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-slate-400">
                            <Paintbrush size={14} className="text-gold" /> Free written estimates
                        </span>
                        <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-slate-400">
                            <Award size={14} className="text-gold" /> 10+ years experience
                        </span>
                        <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-slate-400">
                            <Phone size={14} className="text-gold" /> Response within 24 hours
                        </span>
                    </motion.div>
                </div>
                <div className="relative lg:col-span-5">
                    <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4, duration: 1, ease: EASE }} style={{ y: yImg }} className="clip-frame relative">
                        <img src={mcguire(7)} alt="Grand foyer with double staircase and chandelier — interior painting by B&B Painting and Renovation" className="h-[420px] w-full object-cover lg:h-[560px]" loading="eager" />
                        <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
                    </motion.div>
                    <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.7, duration: 0.9, ease: EASE }} style={{ y: yCard }} className="clip-frame-alt absolute -bottom-10 -left-6 w-52 sm:-left-10 sm:w-64">
                        <img src={caledon(3)} alt="Timber-frame great room with stone fireplace — painted by B&B Painting and Renovation" className="h-40 w-full border-4 border-ink object-cover sm:h-48" />
                    </motion.div>
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8, ease: EASE }} className="absolute -top-6 right-2 hidden items-center gap-3 border border-gold/25 bg-ink-3/90 px-5 py-4 backdrop-blur-md sm:flex">
                        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">Our work · McGuire Trail, Caledon East</span>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

const WHY = [
    ["01", "Detailed written estimates", "Itemized scope, products and schedule — so you always know what you're paying for."],
    ["02", "Prep-first process", "Repairs, sanding, caulking and priming come before a single finish coat."],
    ["03", "Clear communication", "One point of contact, daily site tidying and updates from start to final walkthrough."],
    ["04", "Homes, builders & businesses", "From single rooms to subcontract packages for general contractors and property managers."],
];

const WORK_STRIP = [
    { src: mcguire(15), alt: "Coffered ceiling, wainscoting and arched window — McGuire Trail, Caledon East", badge: "Our work · Caledon East, ON" },
    { src: caledon(17), alt: "Staircase with charcoal accent wall — Caledon Build", badge: "Our work · Caledon, ON" },
    { src: orangeville(1), alt: "Fire-rated coating on structural steel with scaffolding — Orangeville", badge: "Our work · Orangeville, ON" },
];

export default function Home() {
    return (
        <div data-testid="home-page">
            <Hero />
            <Marquee />

            <section className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16 lg:py-32">
                <div className="grid items-center gap-12 lg:grid-cols-2">
                    <Reveal>
                        <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Our company</p>
                        <h2 className="font-display mt-4 text-3xl font-bold tracking-tight sm:text-4xl">
                            Painting done properly — for homes, builders and businesses.
                        </h2>
                        <p className="mt-6 text-base leading-relaxed text-slate-300">
                            B&amp;B Painting and Renovation is an Ontario painting company serving
                            Toronto and the Greater Toronto Area. We handle interior and exterior
                            painting, renovations, new construction projects and commercial work —
                            with the same discipline on every job: protect the site, prepare the
                            surface, apply premium materials, and finish sharp.
                        </p>
                        <p className="mt-4 text-base leading-relaxed text-slate-300">
                            Whether you're a homeowner refreshing a bedroom, a builder needing a
                            dependable subcontractor, or a property manager planning unit turnovers —
                            you get a written estimate, a defined scope, and a crew that treats your
                            property like our own.
                        </p>
                        <Link to="/about" data-testid="home-about-link" className="group mt-8 inline-flex items-center gap-2 border-b border-gold pb-1 font-mono text-xs uppercase tracking-[0.2em] text-gold">
                            More about us <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                        </Link>
                    </Reveal>
                    <Reveal delay={0.12}>
                        <div className="clip-frame overflow-hidden">
                            <img src={caledon(25)} alt="Surface preparation, priming and masking — prep-first process by B&B Painting" className="h-[420px] w-full object-cover" loading="lazy" />
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="border-y border-gold/15 bg-ink-2">
                <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16 lg:py-32">
                    <div className="flex flex-wrap items-end justify-between gap-6">
                        <Reveal>
                            <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Featured services</p>
                            <h2 className="font-display mt-4 max-w-xl text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
                                Every surface, every scope.
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <Link to="/services" data-testid="home-services-link" className="group inline-flex items-center gap-2 border-b border-gold pb-1 font-mono text-xs uppercase tracking-[0.2em] text-gold">
                                All services <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                            </Link>
                        </Reveal>
                    </div>
                    <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {FEATURED.map((idx, i) => {
                            const s = SERVICE_CATEGORIES[idx];
                            return (
                                <Reveal key={s.name} delay={i * 0.06}>
                                    <Link to={`/contact?service=${encodeURIComponent(s.name)}`} data-testid={`home-service-card-${i}`}
                                        onMouseMove={spotlight}
                                        className="spot-card group flex h-full flex-col justify-between border border-gold/15 bg-ink-3 p-8 transition-colors hover:border-gold/50">
                                        <div className="flex items-center justify-between">
                                            <span className="font-mono text-xs text-slate-500">0{i + 1}</span>
                                        </div>
                                        <div className="mt-14">
                                            <h3 className="font-display text-xl font-bold">{s.short}</h3>
                                            <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.desc}</p>
                                            <span className="mt-5 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-gold">
                                                Request quote <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
                                            </span>
                                        </div>
                                    </Link>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16 lg:py-32">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <Reveal>
                        <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Our work</p>
                        <h2 className="font-display mt-4 text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
                            Proof is on the wall.
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <Link to="/portfolio" data-testid="home-portfolio-link" className="group inline-flex items-center gap-2 border-b border-gold pb-1 font-mono text-xs uppercase tracking-[0.2em] text-gold">
                            Full portfolio <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                        </Link>
                    </Reveal>
                </div>
                <div className="mt-12 grid gap-6 md:grid-cols-3">
                    {WORK_STRIP.map((w, i) => (
                        <Reveal key={w.src} delay={i * 0.1}>
                            <div className={`clip-frame ${i === 1 ? "clip-frame-alt" : ""} group relative overflow-hidden`}>
                                <img src={w.src} alt={w.alt} className="h-72 w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                                <span className="absolute left-4 top-4 border border-gold/40 bg-ink/80 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-gold backdrop-blur-sm">
                                    {w.badge}
                                </span>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="border-y border-gold/15 bg-ink-2">
                <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16 lg:py-32">
                    <Reveal>
                        <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Why choose us</p>
                        <h2 className="font-display mt-4 max-w-2xl text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
                            Built on process, not promises.
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid gap-px overflow-hidden border border-gold/15 bg-gold/15 sm:grid-cols-2 lg:grid-cols-4">
                        {WHY.map(([n, t, d], i) => (
                            <Reveal key={n} delay={i * 0.08} className="bg-ink">
                                <div className="h-full p-8">
                                    <span className="font-mono text-sm text-gold">{n}</span>
                                    <h3 className="font-display mt-6 text-xl font-bold">{t}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-slate-400">{d}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="border-b border-gold/15" data-testid="home-google-reviews">
                <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 sm:px-8 lg:grid-cols-12 lg:px-16 lg:py-28">
                    <Reveal className="lg:col-span-4">
                        <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Reviewed on Google</p>
                        <div className="mt-6 flex items-end gap-5">
                            <span className="font-display text-7xl font-extrabold leading-none">5.0</span>
                            <div className="pb-1">
                                <Stars />
                                <span className="mt-2 block font-mono text-xs uppercase tracking-[0.18em] text-slate-400">10 Google reviews</span>
                            </div>
                        </div>
                        <div className="mt-8 flex flex-col gap-4 items-start">
                            <a href={GOOGLE_URL} target="_blank" rel="noopener noreferrer" data-testid="home-google-link"
                                className="group inline-flex items-center gap-2 border-b border-gold pb-1 font-mono text-xs uppercase tracking-[0.2em] text-gold">
                                Read reviews on Google <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                            </a>
                            <a href={GOOGLE_URL} target="_blank" rel="noopener noreferrer" data-testid="home-leave-review-link"
                                className="text-xs text-slate-400 transition-colors hover:text-gold">
                                Happy with our work? Leave us a review →
                            </a>
                        </div>
                    </Reveal>
                    <Reveal delay={0.12} className="lg:col-span-8">
                        <blockquote className="border-l-2 border-gold pl-8 sm:pl-10">
                            <Stars size={14} className="text-slate-400" />
                            <p className="mt-5 font-display text-xl leading-relaxed text-slate-100 sm:text-2xl">
                                "If I can rate this guy more stars I would. I'm so so happy with the
                                painting job he did. I called around and he gave me the best deal to
                                paint my basement. Honestly he did an amazing job and actually used
                                quality paint and materials. Not like most of these painters who do a
                                messy job and use cheap materials. I highly recommend using this
                                company to paint your home."
                            </p>
                            <footer className="mt-5 font-mono text-xs uppercase tracking-[0.2em] text-slate-400">
                                Verified Google review · 5.0
                            </footer>
                        </blockquote>
                    </Reveal>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-4 pb-28 pt-24 sm:px-8 lg:px-16 lg:pt-32">
                <div className="grid items-start gap-14 border border-gold/15 bg-ink-3 p-8 sm:p-12 lg:grid-cols-2 lg:p-16">
                    <Reveal>
                        <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
                            Let's talk about your project.
                        </h2>
                        <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-300 sm:text-base">
                            Tell us what you're planning — we'll respond within 24 hours with next
                            steps and a free, written estimate. No pressure, no obligation.
                        </p>
                        <a href="tel:+16475104012" data-testid="home-phone-cta"
                            className="mt-8 inline-flex items-center gap-3 border border-white/20 px-6 py-4 font-mono text-sm tracking-wider text-slate-100 transition-colors hover:border-gold hover:text-gold">
                            <Phone size={16} className="text-gold" /> 647 510 4012
                        </a>
                        <a href="mailto:Bnbpaintinginc@gmail.com" data-testid="home-email-cta" className="mt-4 block break-all font-mono text-xs text-slate-400 hover:text-gold">
                            Bnbpaintinginc@gmail.com
                        </a>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <QuoteForm />
                    </Reveal>
                </div>
            </section>
        </div>
    );
}